import { formatNumber } from '@/general/helpers/formatNumber';
import { processKilometers } from '@/general/helpers/processProductApiData';
import { stringTokenReplacer } from '@/general/helpers/stringTokenReplacer';
import { ProductDetailsPayload } from '@/types/ProductDetail.type';
import { pluralise } from '@/utils/pluralise';

const WARRANTY_MONTHS_THRESHOLD = 3;
const WARRANTY_KILOMETRES_THRESHOLD = 5000;
const EXTENDED_WARRANTY_KILOMETRES_THRESHOLD = 160000;

/**
 * Determine if the car has manufacturer warranty
 */
export const checkManufacturerWarranty = (vehicle: ProductDetailsPayload): boolean => {
  return (
    vehicle.remaining_warranty_months >= WARRANTY_MONTHS_THRESHOLD &&
    (vehicle.unlimited_kilometres_flag || vehicle.remaining_warranty_kilometres >= WARRANTY_KILOMETRES_THRESHOLD)
  );
};

/**
 * Format the manufacturer warranty
 */
export const getManufacturerWarranty = (vehicle: ProductDetailsPayload): string | null => {
  if (checkManufacturerWarranty(vehicle)) {
    const warrantyString = vehicle.unlimited_kilometres_flag
      ? 'Valid for another {remainingYearsAndMonths} and unlimited kms'
      : 'Valid for another {remainingYearsAndMonths} or a further {warrantyKms} (whichever comes first)';

    return stringTokenReplacer(warrantyString, {
      remainingYearsAndMonths: formatRemainingTime(vehicle.remaining_warranty_months),
      warrantyKms: formatNumber(vehicle.remaining_warranty_kilometres, { append: ' km' }),
    });
  }

  return null;
};

/**
 * Format the extended warranty
 */
export const getExtendedWarranty = (vehicle: ProductDetailsPayload): string | null => {
  const vehicleKms = processKilometers(vehicle.kilometres);

  if (vehicleKms < EXTENDED_WARRANTY_KILOMETRES_THRESHOLD) {
    return 'Eligible for 1, 3 or 5 years of protection starting from $1,190.';
  }

  return null;
};

const formatRemainingTime = (remainingMonths: number | null): string => {
  if (remainingMonths === null || isNaN(remainingMonths)) {
    return '';
  }
  const results: string[] = [];
  const years = Math.floor(remainingMonths / 12);
  const months = remainingMonths % 12;

  if (years >= 1) {
    results.push(pluralise('year', years));
  }

  if (months >= 1) {
    results.push(pluralise('month', months));
  }

  return results.join(' and ');
};
